/**
 * Custom Experience Form Handler
 * Handles phone validation with country calling codes and date validation
 */

class CustomExperienceForm {
  constructor(formElement) {
    // Guard against double initialization (DOMContentLoaded + section reload)
    if (formElement.dataset.cxFormInit) return;
    formElement.dataset.cxFormInit = 'true';

    this.form = formElement;
    this.phoneInput = this.form.querySelector('input[type="tel"]');
    this.startDateInput = this.form.querySelector(
      'input[name="contact[start_date]"]'
    );
    this.endDateInput = this.form.querySelector(
      'input[name="contact[end_date]"]'
    );

    this.init();
  }

  init() {
    if (this.phoneInput) {
      this.initPhoneInput();
    }

    if (this.startDateInput && this.endDateInput) {
      this.initDateValidation();
    }

    // Handle checkbox groups
    this.checkboxGroups = this.form.querySelectorAll('[data-required-group]');
    this.checkboxGroups.forEach((checkbox) => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    });

    // Handle form submission
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Surface the result of the previous submission
    this.initSubmissionFeedback();
  }

  /**
   * Shopify reloads the page after a contact form post. When the form lives in
   * a popup/drawer the panel is closed on reload, so the server-rendered
   * success and error blocks are hidden inside a closed <dialog>: the customer
   * gets no feedback at all. Lift the success message out into a toast, and
   * reopen the panel on errors so the fields can actually be corrected.
   */
  initSubmissionFeedback() {
    const dialog = this.form.closest('dialog');

    const success = this.form.querySelector('.custom-experience-form__success');
    if (success && dialog) {
      this.showSuccessToast(success);
    }

    const error = this.form.querySelector('.custom-experience-form__error');
    if (error && dialog) {
      // Reopen the panel so the customer can see and fix what failed
      customElements.whenDefined('dialog-component').then(() => {
        const dialogComponent = this.form.closest('dialog-component');
        if (
          dialogComponent &&
          typeof dialogComponent.showDialog === 'function'
        ) {
          dialogComponent.showDialog();
        }
      });
    }
  }

  showSuccessToast(success) {
    // Move it out of the closed dialog - a hidden dialog's children aren't
    // rendered, so it has to live on the body to be visible
    document.body.appendChild(success);
    success.classList.add('custom-experience-form__success--toast');

    const onKeydown = (event) => {
      if (event.key === 'Escape') dismiss();
    };

    const dismiss = () => {
      if (!success.isConnected) return;
      clearTimeout(this.successToastTimeout);
      document.removeEventListener('keydown', onKeydown);

      success.classList.add('custom-experience-form__success--dismissing');
      success.addEventListener('animationend', () => success.remove(), {
        once: true,
      });
      // Fallback in case the animation doesn't run (reduced motion, etc.)
      setTimeout(() => success.remove(), 500);
    };

    const closeButton = success.querySelector(
      '.custom-experience-form__success-close'
    );
    if (closeButton) {
      closeButton.addEventListener('click', dismiss);
    }

    document.addEventListener('keydown', onKeydown);

    // Move screen reader focus to the confirmation
    success.focus();

    this.successToastTimeout = setTimeout(dismiss, 10000);
  }

  initPhoneInput() {
    // Wait for intl-tel-input library to be available
    if (typeof intlTelInput !== 'undefined') {
      this.initIntlTelInput();
    } else {
      // Wait for library to load (if loaded via CDN)
      const checkIntlTelInput = setInterval(() => {
        if (typeof intlTelInput !== 'undefined') {
          clearInterval(checkIntlTelInput);
          this.initIntlTelInput();
        }
      }, 100);

      // Stop polling after 2 seconds if the library never loads
      setTimeout(() => {
        clearInterval(checkIntlTelInput);
        if (!this.iti) {
          this.initBasicPhoneValidation();
        }
      }, 2000);
    }

    // Never leave an error the user can't clear: any edit resets validity so
    // the browser's native check (which runs before our submit handler) passes
    // and handleSubmit gets a chance to do the real validation.
    this.phoneInput.addEventListener('input', () => {
      this.phoneInput.setCustomValidity('');
    });

    // Validate on blur, but don't pop a bubble while the user is tabbing away
    this.phoneInput.addEventListener('blur', () => this.validatePhone());
  }

  initIntlTelInput() {
    try {
      this.iti = intlTelInput(this.phoneInput, {
        initialCountry: 'mx',
        preferredCountries: ['mx', 'us'],
        utilsScript:
          'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js',
        separateDialCode: true,
        nationalMode: false,
        showFlags: false,
      });

      // utils.js loads asynchronously; re-validate once it lands so an
      // already-typed number is judged by the real validator
      if (this.iti.promise) {
        this.iti.promise
          .catch(() => {})
          .then(() => {
            if (this.phoneInput.value.trim()) {
              this.validatePhone();
            }
          });
      }

      // Prevent input focus when clicking on the flag container
      // Use a small delay to ensure the flag container is rendered
      setTimeout(() => {
        const flagContainer = this.phoneInput.parentElement.querySelector(
          '.iti__flag-container'
        );

        if (flagContainer) {
          // Prevent mousedown from bubbling to input and causing focus
          // We use stopPropagation instead of preventDefault to allow
          // intl-tel-input's handlers to work normally
          flagContainer.addEventListener(
            'mousedown',
            (e) => {
              // Stop the event from reaching the input element
              e.stopPropagation();
              // Don't prevent default - let intl-tel-input handle the click
            },
            true
          ); // Use capture phase to intercept early
        }
      }, 100);

      // Update the input data attribute when country changes
      this.phoneInput.addEventListener('countrychange', () => {
        const countryData = this.iti.getSelectedCountryData();
        this.phoneInput.setAttribute('data-country-code', countryData.dialCode);
        this.phoneInput.setAttribute('data-country-iso', countryData.iso2);

        // Picking the right country can fix a previously invalid number
        this.validatePhone();
      });

      // Set initial country code
      const initialCountryData = this.iti.getSelectedCountryData();
      if (initialCountryData) {
        this.phoneInput.setAttribute(
          'data-country-code',
          initialCountryData.dialCode
        );
        this.phoneInput.setAttribute(
          'data-country-iso',
          initialCountryData.iso2
        );
      }
    } catch (error) {
      console.warn('Failed to initialize intl-tel-input:', error);
      this.initBasicPhoneValidation();
    }
  }

  initBasicPhoneValidation() {
    // Library unavailable: hint the expected format. No `pattern` attribute -
    // validatePhoneBasic covers this, and pattern would add a second, English
    // browser message that contradicts ours.
    this.phoneInput.setAttribute('placeholder', '+52 55 1234 5678');
  }

  validatePhone({ report = false } = {}) {
    if (!this.phoneInput) return true;

    // Always start from a clean slate so a stale message can never block a
    // number the user has since corrected
    this.phoneInput.setCustomValidity('');

    const phoneValue = this.phoneInput.value.trim();

    if (!phoneValue) {
      // Required field validation will be handled by HTML5
      return true;
    }

    if (this.iti && typeof intlTelInputUtils !== 'undefined') {
      try {
        // isPossibleNumber checks the length is plausible for the country,
        // rather than matching against carrier prefix ranges. The pinned
        // metadata goes stale, so strict validation rejects real numbers.
        if (this.iti.isPossibleNumber()) {
          return true;
        }

        const errorCode = this.iti.getValidationError();
        let errorMessage = this.i18n('invalid', 'Please enter a valid phone number');

        // Provide more specific error messages if available
        const errorMap = intlTelInputUtils.validationError;
        if (errorMap) {
          if (errorCode === errorMap.TOO_SHORT) {
            errorMessage = this.i18n('tooShort', 'The phone number is too short');
          } else if (errorCode === errorMap.TOO_LONG) {
            errorMessage = this.i18n('tooLong', 'The phone number is too long');
          } else if (errorCode === errorMap.INVALID_COUNTRY_CODE) {
            errorMessage = this.i18n('invalidCountry', 'Invalid country code');
          }
        }

        return this.setPhoneError(errorMessage, report);
      } catch (error) {
        // Fall back to a sanity check if the library throws
        return this.validatePhoneBasic(phoneValue, report);
      }
    }

    // Library or utils unavailable: we can't judge the number properly, so
    // only reject obvious garbage
    return this.validatePhoneBasic(phoneValue, report);
  }

  validatePhoneBasic(phoneValue, report = false) {
    const digitsOnly = phoneValue.replace(/\D/g, '');

    if (!/^[\d\s\-+().]+$/.test(phoneValue)) {
      return this.setPhoneError(
        this.i18n('invalid', 'Please enter a valid phone number'),
        report
      );
    }

    // Deliberately low: national number lengths vary by country, and a hard
    // 10-digit floor false-rejects valid numbers
    if (digitsOnly.length < 7) {
      return this.setPhoneError(
        this.i18n('tooShort', 'The phone number is too short'),
        report
      );
    }

    this.phoneInput.setCustomValidity('');
    return true;
  }

  setPhoneError(message, report) {
    this.phoneInput.setCustomValidity(message);
    if (report) {
      this.phoneInput.reportValidity();
    }
    return false;
  }

  /**
   * Reads a translated string rendered into the phone input's data-i18n-*
   * attributes by the Liquid snippet, falling back to English if the
   * attribute is missing (e.g. a stale cached asset).
   */
  i18n(key, fallback) {
    const attrMap = {
      invalid: 'i18nInvalid',
      tooShort: 'i18nTooShort',
      tooLong: 'i18nTooLong',
      invalidCountry: 'i18nInvalidCountry',
    };
    return this.phoneInput.dataset[attrMap[key]] || fallback;
  }

  initDateValidation() {
    // Update end date minimum when start date changes (HTML already sets initial min)
    if (this.startDateInput) {
      this.startDateInput.addEventListener('change', () => {
        const startDate = this.startDateInput.value;
        if (startDate && this.endDateInput) {
          this.endDateInput.setAttribute('min', startDate);

          // If end date is before start date, clear it
          if (this.endDateInput.value && this.endDateInput.value < startDate) {
            this.endDateInput.value = '';
          }
        }
      });
    }

    if (this.endDateInput && this.startDateInput) {
      this.endDateInput.addEventListener(
        'change',
        this.validateEndDate.bind(this)
      );
    }
  }

  validateEndDate() {
    if (!this.endDateInput || !this.startDateInput) return;

    const startDate = this.startDateInput.value;
    const endDate = this.endDateInput.value;

    if (endDate && startDate && endDate < startDate) {
      this.endDateInput.setCustomValidity(
        this.endDateInput.dataset.i18nInvalidRange ||
          'The end date must be on or after the start date'
      );
      return false;
    } else {
      this.endDateInput.setCustomValidity('');
      return true;
    }
  }

  handleCheckboxChange() {
    // Update hidden inputs when checkboxes change
    this.updateCheckboxHiddenInputs();
  }

  updateCheckboxHiddenInputs() {
    // Get all checkbox groups
    const fieldNames = ['interest_topics', 'experience_types'];

    fieldNames.forEach((fieldName) => {
      const checkboxes = this.form.querySelectorAll(
        `[data-field-name="${fieldName}"]:checked`
      );
      const hiddenInput = this.form.querySelector(
        `input[name="contact[${fieldName}]"]`
      );

      if (hiddenInput) {
        const values = Array.from(checkboxes)
          .map((cb) => cb.value)
          .filter((value) => value)
          .join(', ');

        hiddenInput.value = values;
      }
    });
  }

  validateCheckboxGroup(groupName) {
    const checkboxes = this.form.querySelectorAll(
      `[data-required-group="${groupName}"]`
    );
    const checked = Array.from(checkboxes).some((cb) => cb.checked);

    if (!checked) {
      // Set custom validity on first checkbox in group
      if (checkboxes.length > 0) {
        const firstCheckbox = checkboxes[0];
        firstCheckbox.setCustomValidity(
          firstCheckbox.dataset.i18nRequired ||
            'Please select at least one option'
        );

        // Clear validity on others
        Array.from(checkboxes)
          .slice(1)
          .forEach((cb) => {
            cb.setCustomValidity('');
          });
      }
      return false;
    } else {
      // Clear validity on all checkboxes in group
      checkboxes.forEach((cb) => {
        cb.setCustomValidity('');
      });
      return true;
    }
  }

  handleSubmit(event) {
    let isValid = true;

    // Validate phone
    if (this.phoneInput) {
      if (!this.validatePhone({ report: false })) {
        isValid = false;
      }
    }

    // Validate dates
    if (this.startDateInput && this.endDateInput) {
      if (!this.validateEndDate()) {
        isValid = false;
      }
    }

    // Validate checkbox groups
    const checkboxGroups = new Set();
    this.checkboxGroups.forEach((checkbox) => {
      const groupName = checkbox.getAttribute('data-required-group');
      if (groupName) {
        checkboxGroups.add(groupName);
      }
    });

    checkboxGroups.forEach((groupName) => {
      if (!this.validateCheckboxGroup(groupName)) {
        isValid = false;
      }
    });

    // Update checkbox hidden inputs before submission
    this.updateCheckboxHiddenInputs();

    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();

      // Report the first invalid field in DOM order and focus it
      this.form.reportValidity();
      return false;
    }

    // Record the exact page the enquiry came from, including query params
    // (canonical_url in the template strips them)
    const pageUrlInput = this.form.querySelector(
      'input[name="contact[page_url]"]'
    );
    if (pageUrlInput) {
      pageUrlInput.value = window.location.href;
    }

    // If using intl-tel-input, format phone number before submission
    if (this.phoneInput && this.iti && typeof intlTelInput !== 'undefined') {
      try {
        const countryData = this.iti.getSelectedCountryData();
        const countryCode = countryData ? countryData.dialCode : '';

        let fullNumber =
          typeof intlTelInputUtils !== 'undefined' &&
          intlTelInputUtils.numberFormat
            ? this.iti.getNumber(intlTelInputUtils.numberFormat.E164)
            : this.iti.getNumber();

        // getNumber() returns '' when utils never loaded; compose the number
        // ourselves so the store never receives a phone missing its country code
        if (!fullNumber && countryCode) {
          const raw = this.phoneInput.value.trim();
          const digits = raw.replace(/\D/g, '');
          if (digits) {
            // Mirrors the library's own _getFullNumber: only prepend the dial
            // code when the user hasn't already typed one
            fullNumber =
              raw.charAt(0) === '+' ? `+${digits}` : `+${countryCode}${digits}`;
          }
        }

        // Store formatted number
        if (fullNumber) {
          this.phoneInput.value = fullNumber;
        }

        // Add hidden input for country code
        let countryCodeInput = this.form.querySelector(
          'input[name="contact[country_code]"]'
        );
        if (!countryCodeInput && countryCode) {
          countryCodeInput = document.createElement('input');
          countryCodeInput.type = 'hidden';
          countryCodeInput.name = 'contact[country_code]';
          this.form.appendChild(countryCodeInput);
        }
        if (countryCodeInput && countryCode) {
          countryCodeInput.value = countryCode;
        }
      } catch (error) {
        console.warn('Error formatting phone number:', error);
        // Continue with submission even if formatting fails
      }
    }

    return true;
  }
}

function initCustomExperienceForms(root = document) {
  const forms = root.querySelectorAll('.custom-experience-form__form');
  forms.forEach((form) => {
    new CustomExperienceForm(form);
  });
}

// Initialize form when DOM is ready
document.addEventListener('DOMContentLoaded', () => initCustomExperienceForms());

// Re-initialize after a theme editor re-render (the constructor guards against
// initializing the same form twice)
document.addEventListener('shopify:section:load', (event) => {
  initCustomExperienceForms(event.target || document);
});
